import React, { useState, useRef } from 'react';
import { productAPI } from './SellerApi.js';
import './AddProduct.css';

// ── Default empty variant ──────────────────────────
const emptyVariant = () => ({
  id: Date.now() + Math.random(),
  unit: '',
  price: '',
  mrp: '',
  stock_quantity: ''
});

const SellerAddProduct  = () => {
  const [form, setForm] = useState({
    name: '', category: 'Vegetables',
    description: '', product_overview: '', key_benefits: '', usage_instructions: '',
  });

  // Start with 1 variant — user can add up t
const [variants, setVariants] = useState([
  { id: Date.now(), unit: '1 kg', price: '', mrp: '', stock_quantity: '' }
]);

  const [images,   setImages]   = useState([]);   // File objects (max 3)
  const [previews, setPreviews] = useState([]);   // base64 for preview
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');
  const fileRef = useRef(null);

  // ── Form field update ──────────────────────────
  const upForm = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Variant operations ─────────────────────────
  const addVariant = () => {
    if (variants.length >= 5) return;
    setVariants(prev => [...prev, emptyVariant()]);
  };

  const upVariant = (i, key, val) => {
    setVariants(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [key]: val };
      return next;
    });
  };

  // ── Suggested weight labels ─────────────────────
  const WEIGHT_SUGGESTIONS = ['250g', '500g', '1 kg', '2 kg', '5 kg', '10 kg', '1 piece', '1 dozen', '1 litre', '500 ml'];

  // ── Image handling ─────────────────────────────
  const handleImages = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    setImages(files);
    const readers = files.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }));
    Promise.all(readers).then(setPreviews);
  };

  const removeImage = (i) => {
    setImages(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  // ── Validate before submit ─────────────────────
  const validate = () => {
    if (!form.name.trim()) return 'Product name is required.';

    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      if (!v.unit.trim()) return `Variant ${i + 1}: Weight/Unit is required.`;
      const price = parseFloat(v.price);
      if (!v.price || isNaN(price) || price <= 0) return `Variant ${i + 1}: Selling price must be greater than 0.`;
      if (!v.stock_quantity || parseInt(v.stock_quantity) < 0) return `Variant ${i + 1}: Stock quantity is required.`;
    }

    return null;
  };

  // ── Submit ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');

    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setLoading(true);

    try {
      // Build clean variants — all numbers, no empty strings
      const cleanVariants = variants.map(v => ({
        unit:           v.unit.trim(),
        price:          parseFloat(v.price),
        mrp:            parseFloat(v.mrp) || parseFloat(v.price),
        stock_quantity: parseInt(v.stock_quantity) || 0,
      }));

      const fd = new FormData();

      // ── Append text fields ──
      fd.append('name',               form.name.trim());
      fd.append('category',           form.category);
      fd.append('description',        form.description);
      fd.append('product_overview',   form.product_overview);
      fd.append('key_benefits',       form.key_benefits);
      fd.append('usage_instructions', form.usage_instructions);

      // ── Append variants as JSON string ──
      fd.append('variants', JSON.stringify(cleanVariants));

      // ── Append images ──
      if (images[0]) fd.append('image',   images[0]);
      if (images[1]) fd.append('image_2', images[1]);
      if (images[2]) fd.append('image_3', images[2]);

      await productAPI.add(fd);

      setSuccess('✅ Product published successfully!');
      // Reset form
      setForm({ name:'', category:'Vegetables', description:'', product_overview:'', key_benefits:'', usage_instructions:'' });
      setVariants([{
  id: Date.now(),
  unit:'1 kg',
  price:'',
  mrp:'',
  stock_quantity:''
}]);
      setImages([]); setPreviews([]);

    } catch (err) {
      setError(err.message || 'Failed to publish product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ap-wrapper">
      <div className="ap-header">
        <h2>Add New Product</h2>
        <p>List your farm produce on the ATR marketplace</p>
      </div>

      {error   && <div className="ap-alert ap-error">⚠ {error}</div>}
      {success && <div className="ap-alert ap-success">{success}</div>}

      <form onSubmit={handleSubmit} className="ap-form-grid">

        {/* ── LEFT COLUMN ── */}
        <div className="ap-column-main">

          {/* General Info */}
          <section className="ap-card">
            <h3 className="ap-card-title">General Information</h3>
            <div className="ap-input-group">
              <label>Product Name *</label>
              <input type="text" placeholder="e.g. Organic Red Tomatoes"
                value={form.name} onChange={e => upForm('name', e.target.value)} required />
            </div>
            <div className="ap-input-group">
              <label>Category</label>
              <select value={form.category} onChange={e => upForm('category', e.target.value)}>
                {['Vegetables','Fruits','Seeds','Grains','Spices','Dairy','Nuts','Herbs','Equipment','Others'].map(c => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="ap-input-group">
              <label>Short Description</label>
              <textarea rows={2} placeholder="Brief summary of the product..."
                value={form.description} onChange={e => upForm('description', e.target.value)} />
            </div>
          </section>

          {/* Variants / Pricing */}
          <section className="ap-card">
            <div className="ap-card-header">
              <h3 className="ap-card-title">
                Weight Variants & Pricing
                <span className="ap-variant-count">{variants.length}/5</span>
              </h3>
              {variants.length < 5 && (
                <button type="button" onClick={addVariant} className="ap-btn-add">
                  + Add Weight
                </button>
              )}
            </div>
            <p className="ap-card-hint">Add different weight options (e.g. 250g, 500g, 1 kg). Each gets its own price.</p>

{variants.map((v, i) => (
  <div key={v.id} className="ap-variant-block">
                    <div className="ap-variant-label">
                  <span>Variant {i + 1}</span>
                  {i === 0 && <span className="ap-primary-tag">Primary</span>}
                </div>

                <div className="ap-variant-inputs">
                  {/* Weight/Unit with suggestions */}
                  <div className="ap-input-subgroup">
                    <label>Weight / Unit *</label>
                    <input
                      list={`weights-${i}`}
                      type="text"
                      placeholder="e.g. 500g, 1 kg"
                      value={v.unit}
                      onChange={e => upVariant(i, 'unit', e.target.value)}
                      required
                    />
                    <datalist id={`weights-${i}`}>
                      {WEIGHT_SUGGESTIONS.map(w => <option key={w} value={w} />)}
                    </datalist>
                  </div>

                  {/* Selling Price */}
                  <div className="ap-input-subgroup">
                    <label>Selling Price (₹) *</label>
                    <div className="ap-price-input">
                      <span className="ap-currency">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={v.price}
                        onChange={e => upVariant(i, 'price', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* MRP */}
                  <div className="ap-input-subgroup">
                    <label>MRP (₹) <span className="ap-optional">(optional)</span></label>
                    <div className="ap-price-input">
                      <span className="ap-currency">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={v.mrp}
                        onChange={e => upVariant(i, 'mrp', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Stock */}
                  <div className="ap-input-subgroup">
                    <label>Stock Qty *</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="e.g. 50"
                      value={v.stock_quantity}
                      onChange={e => upVariant(i, 'stock_quantity', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Discount preview */}
                {v.price && v.mrp && parseFloat(v.mrp) > parseFloat(v.price) && (
                  <div className="ap-discount-preview">
                    Discount: {Math.round(((v.mrp - v.price) / v.mrp) * 100)}% off MRP
                  </div>
                )}

              </div>
            ))}
          </section>

          {/* Product Details */}
          <section className="ap-card">
            <h3 className="ap-card-title">Product Details</h3>
            <div className="ap-input-group">
              <label>Product Overview</label>
              <textarea rows={3} placeholder="Describe the product in detail..."
                value={form.product_overview} onChange={e => upForm('product_overview', e.target.value)} />
            </div>
            <div className="ap-input-group">
              <label>Key Benefits</label>
              <textarea rows={3} placeholder="Why should customers buy this?"
                value={form.key_benefits} onChange={e => upForm('key_benefits', e.target.value)} />
            </div>
            <div className="ap-input-group">
              <label>Usage Instructions</label>
              <textarea rows={2} placeholder="How to use or store this product..."
                value={form.usage_instructions} onChange={e => upForm('usage_instructions', e.target.value)} />
            </div>
          </section>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="ap-column-side">

          {/* Images */}
          <section className="ap-card">
            <h3 className="ap-card-title">Product Images <span className="ap-optional">(max 3)</span></h3>
            <div className="ap-upload-zone" onClick={() => fileRef.current?.click()}>
              <div className="ap-upload-icon">📸</div>
              <p>Click to upload product photos</p>
              <span>JPG, PNG, WEBP · Max 5MB each</span>
              <input ref={fileRef} type="file" multiple accept="image/*" hidden onChange={handleImages} />
            </div>

            {previews.length > 0 && (
              <div className="ap-preview-grid">
                {previews.map((src, i) => (
                  <div key={i} className="ap-preview-item">
                    <img src={src} alt={`preview ${i + 1}`} />
                    <button type="button" className="ap-preview-remove" onClick={() => removeImage(i)}>×</button>
                    {i === 0 && <span className="ap-primary-img-tag">Main</span>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Pricing summary */}
          {variants[0]?.price && (
            <section className="ap-card ap-summary-card">
              <h3 className="ap-card-title">Pricing Summary</h3>
              {variants.map((v, i) => v.price ? (
                <div key={i} className="ap-summary-row">
                  <span className="ap-summary-weight">{v.unit || `Variant ${i+1}`}</span>
                  <span className="ap-summary-price">₹{v.price}</span>
                  {v.mrp && parseFloat(v.mrp) > parseFloat(v.price) && (
                    <span className="ap-summary-disc">
                      {Math.round(((v.mrp - v.price) / v.mrp) * 100)}% off
                    </span>
                  )}
                </div>
              ) : null)}
            </section>
          )}

          {/* Submit */}
          <button type="submit" className="ap-btn-primary" disabled={loading}>
            {loading ? (
              <><span className="ap-spinner" /> Publishing...</>
            ) : (
              '🚀 Publish Product'
            )}
          </button>

          <p className="ap-submit-note">
            Your product will be visible on the marketplace immediately after publishing.
          </p>
        </div>
      </form>
    </div>
  );
};

export default SellerAddProduct;
